// BESCHAU — Beschleunigter Schema-Check und Auswertung Utility
// ADR-060: Zod drop-in with maelstrom::validate backend (Phase 2)
// Phase 1: Zod delegation shim

import { z } from 'zod';

// Re-export the core Zod type for consumers that need to type schema params
export type BeschaParser = z.ZodTypeAny;

export const b = {
  string: (): z.ZodString => z.string(),

  number: (): z.ZodNumber => z.number(),

  boolean: (): z.ZodBoolean => z.boolean(),

  null: (): z.ZodNull => z.null(),

  unknown: (): z.ZodUnknown => z.unknown(),

  any: (): z.ZodAny => z.any(),

  literal: <T extends string | number | boolean | null | bigint>(val: T): z.ZodLiteral<T> =>
    z.literal(val),

  enum: (values: [string, ...string[]]): z.ZodEnum<any> => z.enum(values as any),

  object: <T extends z.ZodRawShape>(shape: T): z.ZodObject<T> => z.object(shape),

  array: <T extends z.ZodTypeAny>(inner: T): z.ZodArray<T> => z.array(inner),

  union: <T extends readonly [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]>(
    members: T,
  ): z.ZodUnion<T> => z.union(members),

  discriminatedUnion: (
    discriminator: string,
    options: readonly [z.ZodTypeAny, ...z.ZodTypeAny[]],
  ): z.ZodTypeAny =>
    // Zod v4 internal type constraints require widening here
    z.discriminatedUnion(discriminator, options as Parameters<typeof z.discriminatedUnion>[1]),

  record: (key: z.ZodTypeAny, val: z.ZodTypeAny): z.ZodTypeAny =>
    // Zod v4 internal $ZodRecordKey constraint requires widening
    z.record(key as Parameters<typeof z.record>[0], val),

  instanceof: <T extends abstract new (...args: any[]) => any>(
    cls: T,
  ): z.ZodType<InstanceType<T>> => z.instanceof(cls),

  /**
   * Returns schema.nullable() — preferred per the EKELHAFT null-only pattern
   * (T | null, never T | undefined).
   */
  nullable: <T extends z.ZodTypeAny>(schema: T): z.ZodNullable<T> => schema.nullable(),

  /**
   * Returns schema.optional() — provided for completeness but prefer b.nullable().
   */
  optional: <T extends z.ZodTypeAny>(schema: T): z.ZodOptional<T> => schema.optional(),
} as const;

// Augment b with the `infer` type helper (cannot be on a const directly)
export declare namespace b {
  type infer<T extends z.ZodTypeAny> = z.infer<T>;
}

/** Prose-style alias: `import { beschau } from '@ekelhaft-tools/beschau'` */
export const beschau = b;
